import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { ControlledFilledTextField, HFilledTextField } from '@ui/components/common/TextField';
import { HTimePicker } from '@ui/components/common/Pickers';
import FormValidationMessage from '@ui/components/FormValidationMessage';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { useParams } from 'react-router-dom';
import { EventDocumentType } from '@application/enums/event-document-type';
import { CountryEnum } from '@application/enums/country-enum';
import { getVDKEventByIdForEventDetailsAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { createPickupLocationAsync, updatePickupLocationAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { FamilyFestCreatePickupLocationAsync, FamilyFestUpdatePickupLocationAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { toast } from 'react-toastify';
import pickupLocationAPI from '@infrastructure/pickup-location-api';
import { pdokSuggest, pdokLookup, parsePdokCoordinates } from '@infrastructure/pdok-service';
import { v4 as uuidv4 } from 'uuid';
import { Icon } from '@iconify/react';
import { CollaboratorRole } from '@application/enums/collaborator-role';
import { EventTypeEnum } from '@application/enums/event-type-enum';
const AUTOCOMPLETE_MIN_LENGTH = 2;
const AUTOCOMPLETE_DEBOUNCE_MS = 350;
const getCompanyRegionGroups = (companies) => {
    const regionGroups = {};
    companies.forEach((company) => {
        const regionLabel = company.regionLabel || '';
        if (!regionGroups[regionLabel]) {
            regionGroups[regionLabel] = [];
        }
        regionGroups[regionLabel].push(String(company.value));
    });
    return regionGroups;
};
const NewPickupLocationDialog = ({ open, onClose, onSuccess, editingPickupLocation }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    const { pickupLocations } = useAppSelector((state) => state.pickupLocation);
    const companiesByRegion = useAppSelector((state) => state.company.companiesByRegion);
    const [sessionToken, setSessionToken] = useState(uuidv4());
    const [addressInput, setAddressInput] = useState('');
    const [addressOptions, setAddressOptions] = useState([]);
    const [selectedPlaceDetails, setSelectedPlaceDetails] = useState(null);
    const [isAddressSuggestionsLoading, setIsAddressSuggestionsLoading] = useState(false);
    const [isPlaceDetailsLoading, setIsPlaceDetailsLoading] = useState(false);
    const isEditMode = editingPickupLocation != null;
    const previousCountryRef = useRef(null);
    const getCountryOptions = () => [
        {
            label: t('countries.netherlands'),
            value: 'nl',
            icon: (_jsx(Box, { component: 'img', src: '/assets/images/flags/nl-flag.svg', alt: t('countries.netherlands'), sx: { width: 20, height: 15, borderRadius: 0.5 } }))
        },
        {
            label: t('countries.germany') || 'Duitsland',
            value: 'de',
            icon: (_jsx(Box, { component: 'img', src: '/assets/images/flags/de-flag.svg', alt: t('countries.germany'), sx: { width: 20, height: 15, borderRadius: 0.5 } }))
        },
        {
            label: t('countries.belgium'),
            value: 'be',
            icon: (_jsx(Box, { component: 'img', src: '/assets/images/flags/be-flag.svg', alt: t('countries.belgium'), sx: { width: 20, height: 12, borderRadius: 0.5, paddingLeft: '2px' } }))
        },
        {
            label: t('countries.sweden'),
            value: 'se',
            icon: (_jsx(Box, { component: 'img', src: '/assets/images/flags/se-flag.svg', alt: t('countries.sweden'), sx: { width: 20, height: 15, borderRadius: 0.5 } }))
        },
        {
            label: t('countries.austria'),
            value: 'at',
            icon: (_jsx(Box, { component: 'img', src: '/assets/images/flags/at-flag.svg', alt: t('countries.austria'), sx: { width: 20, height: 15, borderRadius: 0.5 } }))
        },
        {
            label: t('countries.switzerland'),
            value: 'ch',
            icon: (_jsx(Box, { component: 'img', src: '/assets/images/flags/ch-flag.svg', alt: t('countries.switzerland'), sx: { width: 20, height: 20, borderRadius: 0.5 } }))
        }
    ];
    const { control, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            eventName: '',
            pickupLocationName: '',
            country: 'nl',
            pickupAddress: null,
            editpickupAddress: null,
            companies: [],
            supplier: null,
            timeOfPickup: null,
            isAccommodationAvailable: false
        }
    });
    const selectedCountry = useWatch({ control, name: 'country' });
    const companyOptions = useMemo(() => {
        const eventCompanies = VDKEventsForEventDetails?.eventCompanies ?? [];
        const eventCompanyIds = new Set(eventCompanies.map((company) => String(company.value)));
        const groupedOptions = (companiesByRegion ?? []).flatMap((region) => (region.companies ?? [])
            .filter((company) => eventCompanyIds.has(String(company.id)))
            .map((company) => ({
            label: company.name,
            value: String(company.id),
            regionKey: region.regionKey,
            regionLabel: region.regionLabel
        })));
        if (groupedOptions.length > 0)
            return groupedOptions;
        return eventCompanies.map((company) => ({
            label: company.label,
            value: String(company.value)
        }));
    }, [VDKEventsForEventDetails?.eventCompanies, companiesByRegion]);
    const regionGroups = useMemo(() => getCompanyRegionGroups(companyOptions), [companyOptions]);
    const handleCompanyRegionToggle = useCallback((regionLabel, field) => {
        const regionCompanyIds = regionGroups[regionLabel] || [];
        if (!regionCompanyIds.length)
            return;
        const currentCompanies = field.value || [];
        const currentCompanyIds = currentCompanies.map((c) => String(typeof c === 'object' && c.value ? c.value : c));
        const allSelected = regionCompanyIds.every((id) => currentCompanyIds.includes(id));
        let updatedSelection;
        if (allSelected) {
            updatedSelection = currentCompanies.filter((c) => {
                const id = String(typeof c === 'object' && c.value ? c.value : c);
                return !regionCompanyIds.includes(id);
            });
        }
        else {
            const newCompanies = companyOptions.filter((opt) => regionCompanyIds.includes(String(opt.value)));
            const merged = [...currentCompanies, ...newCompanies];
            const seen = new Set();
            updatedSelection = merged.filter((c) => {
                const id = String(typeof c === 'object' && c.value ? c.value : c);
                if (seen.has(id))
                    return false;
                seen.add(id);
                return true;
            });
        }
        field.onChange(updatedSelection);
    }, [regionGroups, companyOptions]);
    const selectedCompanyIdsForEdit = useMemo(() => (editingPickupLocation?.eventCompanies ?? [])
        .map((company) => String(company.companyId).trim().toLowerCase())
        .filter((id) => Boolean(id)), [editingPickupLocation]);
    const supplierOptions = useMemo(() => (VDKEventsForEventDetails?.assignedCollaborators ?? []).filter((collaborator) => collaborator.role === CollaboratorRole.TransportManager).map((collaborator) => ({
        label: `${collaborator.firstName} ${collaborator.lastName}`.trim(),
        value: collaborator.id
    })), [VDKEventsForEventDetails?.assignedCollaborators]);
    const normalizedCountry = useMemo(() => {
        const country = String(selectedCountry ?? 'nl')
            .toLowerCase()
            .trim();
        if (country === 'netherlands' || country === 'nl')
            return 'nl';
        if (country === 'germany' || country === 'de')
            return 'de';
        if (country === 'belgium' || country === 'be')
            return 'be';
        if (country === 'sweden' || country === 'se')
            return 'se';
        if (country === 'austria' || country === 'at')
            return 'at';
        if (country === 'switzerland' || country === 'ch')
            return 'ch';
        return 'nl';
    }, [selectedCountry]);
    const selectedCountryEnum = useMemo(() => {
        switch (normalizedCountry) {
            case 'de':
                return CountryEnum.Germany;
            case 'be':
                return CountryEnum.Belgium;
            case 'se':
                return CountryEnum.Sweden;
            case 'at':
                return CountryEnum.Austria;
            case 'ch':
                return CountryEnum.Switzerland;
            case 'nl':
            default:
                return CountryEnum.Netherlands;
        }
    }, [normalizedCountry]);
    useEffect(() => {
        if (!open) {
            previousCountryRef.current = null;
            return;
        }
        if (isEditMode)
            return;
        if (previousCountryRef.current == null) {
            previousCountryRef.current = normalizedCountry;
            return;
        }
        if (previousCountryRef.current === normalizedCountry)
            return;
        previousCountryRef.current = normalizedCountry;
        setValue('pickupAddress', null);
        setAddressInput('');
        setAddressOptions([]);
        setSelectedPlaceDetails(null);
        setSessionToken(uuidv4());
    }, [open, normalizedCountry, setValue, isEditMode]);
    useEffect(() => {
        if (!eventId || VDKEventsForEventDetails?.id === eventId) {
            return;
        }
        dispatch(getVDKEventByIdForEventDetailsAsync({
            id: eventId,
            documentType: EventDocumentType.BackgroundImage
        }));
    }, [dispatch, eventId, VDKEventsForEventDetails?.id]);
    useEffect(() => {
        if (!open) {
            return;
        }
        const eventName = VDKEventsForEventDetails?.title ?? '';
        if (isEditMode && editingPickupLocation) {
            const existingAddressLabel = editingPickupLocation.description ||
                editingPickupLocation.address ||
                t('events.eventDetails.transportTab.pickupAddress');
            const existingAddressOption = {
                label: existingAddressLabel,
                value: editingPickupLocation.googlePlaceId ||
                    editingPickupLocation.id ||
                    existingAddressLabel
            };
            const selectedCompanyIds = new Set(selectedCompanyIdsForEdit.map((id) => String(id).toLowerCase()));
            const selectedCompaniesFromOptions = companyOptions.filter((company) => selectedCompanyIds.has(String(company.value).toLowerCase()));
            const matchedSupplierOption = supplierOptions.find((option) => String(option.value).trim().toLowerCase() ===
                String(editingPickupLocation.supplierId ?? '')
                    .trim()
                    .toLowerCase());
            const matchedSupplierByName = supplierOptions.find((option) => String(option.label).trim().toLowerCase() ===
                String(editingPickupLocation.supplierName ?? '')
                    .trim()
                    .toLowerCase());
            setValue('eventName', eventName);
            setValue('pickupLocationName', editingPickupLocation.name || '');
            const countryCodeForField = editingPickupLocation.country === CountryEnum.Germany
                ? 'de'
                : editingPickupLocation.country === CountryEnum.Belgium
                    ? 'be'
                    : editingPickupLocation.country === CountryEnum.Sweden
                        ? 'se'
                        : 'nl';
            setValue('country', countryCodeForField);
            setValue('pickupAddress', existingAddressOption);
            setValue('editpickupAddress', existingAddressOption.label);
            setValue('companies', selectedCompaniesFromOptions);
            setValue('supplier', matchedSupplierOption?.value != null
                ? String(matchedSupplierOption.value)
                : matchedSupplierByName?.value != null
                    ? String(matchedSupplierByName.value)
                    : editingPickupLocation.supplierId ?? null);
            setValue('isAccommodationAvailable', Boolean(editingPickupLocation.isAccommodationAvailable));
            setValue('timeOfPickup', editingPickupLocation.timeOfPickup ?? null);
            setAddressInput(existingAddressOption.label);
            setAddressOptions([existingAddressOption]);
            setSelectedPlaceDetails({
                placeId: editingPickupLocation.googlePlaceId || existingAddressOption.value,
                formattedAddress: editingPickupLocation.address || existingAddressOption.label,
                latitude: editingPickupLocation.latitude ?? null,
                longitude: editingPickupLocation.longitude ?? null
            });
            return;
        }
        setValue('eventName', eventName);
        setValue('pickupLocationName', '');
        setValue('country', 'nl');
        setValue('pickupAddress', null);
        setValue('companies', []);
        setValue('supplier', null);
        setValue('timeOfPickup', null);
        setValue('isAccommodationAvailable', false);
        setSessionToken(uuidv4());
        setAddressInput('');
        setAddressOptions([]);
        setSelectedPlaceDetails(null);
    }, [
        open,
        setValue,
        VDKEventsForEventDetails?.title,
        isEditMode,
        editingPickupLocation,
        t,
        companyOptions,
        selectedCompanyIdsForEdit,
        supplierOptions
    ]);
    useEffect(() => {
        if (!open || isEditMode)
            return;
        const searchTerm = addressInput.trim();
        if (searchTerm.length < AUTOCOMPLETE_MIN_LENGTH) {
            setAddressOptions([]);
            setIsAddressSuggestionsLoading(false);
            return;
        }
        const timeout = window.setTimeout(async () => {
            try {
                setIsAddressSuggestionsLoading(true);
                if (normalizedCountry === 'nl') {
                    const docs = await pdokSuggest(searchTerm);
                    setAddressOptions(docs.map((doc) => ({ label: doc.weergavenaam, value: doc.id })));
                }
                else {
                    const response = await pickupLocationAPI.getPlacesAutocomplete({
                        input: searchTerm,
                        country: normalizedCountry,
                        sessiontoken: sessionToken,
                        language: i18n.language
                    });
                    const predictions = response.data?.predictions ?? [];
                    setAddressOptions(predictions.map((prediction) => ({
                        label: prediction.description,
                        value: prediction.place_id
                    })));
                }
            }
            catch (error) {
                setAddressOptions([]);
                toast.error(error?.response?.data?.error ||
                    t('events.eventDetails.transportTab.failedToLoadAddressSuggestions'));
            }
            finally {
                setIsAddressSuggestionsLoading(false);
            }
        }, AUTOCOMPLETE_DEBOUNCE_MS);
        return () => {
            window.clearTimeout(timeout);
        };
    }, [addressInput, i18n.language, normalizedCountry, open, sessionToken, t, isEditMode]);
    const onSubmit = async (values) => {
        if (!eventId) {
            toast.error(t('events.eventDetails.transportTab.eventNotFound'));
            return;
        }
        if (!String(values.eventName ?? '').trim()) {
            toast.error(t('validations.required', {
                field: t('events.eventDetails.transportTab.eventName') || 'Event name'
            }));
            return;
        }
        if (!String(values.pickupLocationName ?? '').trim()) {
            toast.error(t('validations.required', {
                field: t('events.eventDetails.transportTab.pickupLocationName') || 'Pickup Location Name'
            }));
            return;
        }
        if (values.country == null || String(values.country).trim() === '') {
            toast.error(t('validations.required', {
                field: t('events.eventDetails.transportTab.country') || 'Country'
            }));
            return;
        }
        const selectedAddress = values.pickupAddress;
        if (!selectedAddress?.value) {
            toast.error(t('validations.required', {
                field: t('events.eventDetails.transportTab.pickupAddress')
            }));
            return;
        }
        if (!Array.isArray(values.companies) || values.companies.length === 0) {
            toast.error(t('validations.required', {
                field: t('events.eventDetails.transportTab.companies') || 'Companies'
            }));
            return;
        }
        if (VDKEventsForEventDetails?.type !== EventTypeEnum.Family) {
            if (values.supplier == null || String(values.supplier).trim() === '') {
                toast.error(t('validations.required', {
                    field: t('events.eventDetails.transportTab.supplier') || 'Supplier'
                }));
                return;
            }
        }
        const timeOfPickupPayload = values.timeOfPickup ?? null;
        try {
            let placeDetails = selectedPlaceDetails;
            if (!placeDetails || placeDetails.placeId !== selectedAddress.value) {
                setIsPlaceDetailsLoading(true);
                if (normalizedCountry === 'nl') {
                    const detail = await pdokLookup(selectedAddress.value);
                    const { lat, lng } = parsePdokCoordinates(detail?.centroide_ll ?? '');
                    placeDetails = {
                        placeId: detail?.id ?? selectedAddress.value,
                        formattedAddress: detail?.weergavenaam ?? selectedAddress.label,
                        latitude: lat,
                        longitude: lng
                    };
                }
                else {
                    const detailsResponse = await pickupLocationAPI.getPlaceDetails({
                        placeId: selectedAddress.value,
                        sessiontoken: sessionToken,
                        language: i18n.language
                    });
                    const result = detailsResponse.data?.result;
                    placeDetails = {
                        placeId: result?.place_id ?? selectedAddress.value,
                        formattedAddress: result?.formatted_address ?? selectedAddress.label,
                        latitude: result?.geometry?.location?.lat ?? null,
                        longitude: result?.geometry?.location?.lng ?? null
                    };
                }
            }
            const isFamilyEvent = VDKEventsForEventDetails?.type === EventTypeEnum.Family;
            if (isEditMode && editingPickupLocation) {
                const selectedCompanyIds = (values.companies ?? [])
                    .map((company) => String(company.value))
                    .filter(Boolean);
                const eventCompanyIds = selectedCompanyIds.length > 0
                    ? Array.from(new Set(selectedCompanyIds))
                    : Array.from(new Set(selectedCompanyIdsForEdit));
                if (isFamilyEvent) {
                    await dispatch(FamilyFestUpdatePickupLocationAsync({
                        id: editingPickupLocation.id,
                        name: values.pickupLocationName.trim(),
                        address: editingPickupLocation.address,
                        country: editingPickupLocation.country,
                        eventCompanyIds,
                        supplierId: values.supplier ? String(values.supplier) : undefined,
                        isAccommodationAvailable: values.isAccommodationAvailable,
                        timeOfPickup: timeOfPickupPayload ?? undefined,
                        isClosed: editingPickupLocation.isClosed ?? false,
                        sortOrder: editingPickupLocation.sortOrder ?? 0
                    })).unwrap();
                }
                else {
                    await dispatch(updatePickupLocationAsync({
                        id: editingPickupLocation.id,
                        eventId,
                        name: values.pickupLocationName.trim(),
                        address: editingPickupLocation.address,
                        country: editingPickupLocation.country,
                        eventCompanyIds,
                        supplierId: values.supplier ? String(values.supplier) : null,
                        isAccommodationAvailable: values.isAccommodationAvailable,
                        timeOfPickup: timeOfPickupPayload
                    })).unwrap();
                }
            }
            else {
                if (isFamilyEvent) {
                    await dispatch(FamilyFestCreatePickupLocationAsync({
                        name: values.pickupLocationName.trim(),
                        address: placeDetails.formattedAddress,
                        country: selectedCountryEnum,
                        latitude: placeDetails.latitude ?? undefined,
                        longitude: placeDetails.longitude ?? undefined,
                        googlePlaceId: placeDetails.placeId,
                        description: selectedAddress.label,
                        locationDisplayText: selectedAddress.label,
                        sortOrder: (pickupLocations?.items?.length ?? 0) + 1,
                        eventCompanyIds: (values.companies ?? [])
                            .map((company) => String(company.value))
                            .filter(Boolean),
                        supplierId: values.supplier ? String(values.supplier) : undefined,
                        isAccommodationAvailable: values.isAccommodationAvailable,
                        timeOfPickup: timeOfPickupPayload ?? undefined
                    })).unwrap();
                }
                else {
                    await dispatch(createPickupLocationAsync({
                        eventId,
                        name: values.pickupLocationName.trim(),
                        address: placeDetails.formattedAddress,
                        country: selectedCountryEnum,
                        latitude: placeDetails.latitude,
                        longitude: placeDetails.longitude,
                        googlePlaceId: placeDetails.placeId,
                        description: null,
                        locationDisplayText: selectedAddress.label,
                        sortOrder: (pickupLocations?.items?.length ?? 0) + 1,
                        eventCompanyIds: (values.companies ?? [])
                            .map((company) => String(company.value))
                            .filter(Boolean),
                        supplierId: values.supplier ? String(values.supplier) : null,
                        isAccommodationAvailable: values.isAccommodationAvailable,
                        timeOfPickup: timeOfPickupPayload
                    })).unwrap();
                }
            }
            toast.success(isEditMode
                ? 'Pickup location updated successfully.'
                : t('events.eventDetails.transportTab.pickupLocationCreated') ||
                    'Pickup location created successfully.');
            reset({
                eventName: VDKEventsForEventDetails?.title ?? '',
                pickupLocationName: '',
                pickupAddress: null,
                companies: [],
                supplier: null,
                timeOfPickup: null,
                isAccommodationAvailable: false
            });
            setAddressInput('');
            setAddressOptions([]);
            setSelectedPlaceDetails(null);
            setSessionToken(uuidv4());
            onClose();
            onSuccess?.();
        }
        catch (error) {
            toast.error(error?.error ||
                error?.message ||
                t('events.eventDetails.transportTab.createPickupLocationFailed'));
        }
        finally {
            setIsPlaceDetailsLoading(false);
        }
    };
    const handleCancel = () => {
        reset({
            eventName: VDKEventsForEventDetails?.title ?? '',
            pickupLocationName: '',
            pickupAddress: null,
            companies: [],
            supplier: null,
            timeOfPickup: null,
            isAccommodationAvailable: false
        });
        setAddressInput('');
        setAddressOptions([]);
        setSelectedPlaceDetails(null);
        setSessionToken(uuidv4());
        onClose();
    };
    const formContent = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', name: 'eventName', control: control, label: t('events.eventDetails.transportTab.eventName') || 'Event name', rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.eventName')
                    })
                }, errorMessage: errors.eventName?.message, disabled: true }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'pickupLocationName', control: control, label: t('events.eventDetails.transportTab.pickupLocationName'), placeholder: t('events.eventDetails.transportTab.pickupLocationName'), rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.pickupLocationName')
                    })
                }, errorMessage: errors.pickupLocationName?.message }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'country', label: t('events.eventDetails.transportTab.country'), showSelectedIcon: true, options: getCountryOptions(), disabled: isEditMode, error: errors.country?.message, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.country')
                    })
                } }), !isEditMode && (_jsx(Controller, { name: 'pickupAddress', control: control, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.pickupAddress')
                    })
                }, render: ({ field }) => (_jsx(Autocomplete, { options: addressOptions, value: field.value, inputValue: addressInput, disabled: isEditMode, onInputChange: (_, value, reason) => {
                        setAddressInput(value);
                        if (reason === 'input') {
                            setSelectedPlaceDetails(null);
                        }
                        if (reason === 'clear') {
                            field.onChange(null);
                            setSelectedPlaceDetails(null);
                        }
                    }, onChange: async (_, newValue) => {
                        field.onChange(newValue);
                        setSelectedPlaceDetails(null);
                        if (!newValue?.value) {
                            return;
                        }
                        try {
                            setIsPlaceDetailsLoading(true);
                            if (normalizedCountry === 'nl') {
                                const detail = await pdokLookup(newValue.value);
                                const { lat, lng } = parsePdokCoordinates(detail?.centroide_ll ?? '');
                                setSelectedPlaceDetails({
                                    placeId: detail?.id ?? newValue.value,
                                    formattedAddress: detail?.weergavenaam ?? newValue.label,
                                    latitude: lat,
                                    longitude: lng
                                });
                            }
                            else {
                                const detailsResponse = await pickupLocationAPI.getPlaceDetails({
                                    placeId: newValue.value,
                                    sessiontoken: sessionToken,
                                    language: i18n.language
                                });
                                const result = detailsResponse.data?.result;
                                const formattedAddress = result?.formatted_address ?? newValue.label;
                                setSelectedPlaceDetails({
                                    placeId: result?.place_id ?? newValue.value,
                                    formattedAddress,
                                    latitude: result?.geometry?.location?.lat ?? null,
                                    longitude: result?.geometry?.location?.lng ?? null
                                });
                                setAddressInput(formattedAddress);
                                field.onChange({ ...newValue, label: formattedAddress });
                            }
                        }
                        catch (error) {
                            toast.error(error?.response?.data?.error ||
                                t('events.eventDetails.transportTab.failedToLoadPlaceDetails'));
                        }
                        finally {
                            setIsPlaceDetailsLoading(false);
                        }
                    }, getOptionLabel: (option) => option?.label ?? '', isOptionEqualToValue: (option, value) => option.value === value.value, loading: isAddressSuggestionsLoading, noOptionsText: addressInput.trim().length < AUTOCOMPLETE_MIN_LENGTH
                        ? t('events.eventDetails.transportTab.startTypingForSuggestions')
                        : t('labels.noOptions'), popupIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill' }), sx: {
                        '& .MuiFilledInput-root .MuiAutocomplete-endAdornment': {
                            right: '3px'
                        }
                    }, renderInput: (params) => (_jsx(HFilledTextField, { ...params, bg: 'grey8', label: t('events.eventDetails.transportTab.pickupAddress'), placeholder: t('events.eventDetails.transportTab.searchPickup'), error: !!errors.pickupAddress?.message, helperText: errors.pickupAddress?.message, slotProps: {
                            input: {
                                ...params.InputProps,
                                endAdornment: (_jsxs(_Fragment, { children: [(isAddressSuggestionsLoading || isPlaceDetailsLoading) && (_jsx(CircularProgress, { color: 'inherit', size: 20, style: { marginBottom: '15px' } })), params.InputProps.endAdornment] }))
                            }
                        } })) })) })), isEditMode && (_jsx(ControlledFilledTextField, { bg: 'grey8', name: 'editpickupAddress', control: control, label: t('events.eventDetails.transportTab.pickupAddress') || 'Pickup  Address', rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.pickupAddress')
                    })
                }, errorMessage: errors.pickupAddress?.message, disabled: true })), _jsx(Controller, { name: 'companies', control: control, rules: {
                    validate: (value) => (Array.isArray(value) && value.length > 0) ||
                        t('validations.required', {
                            field: t('events.eventDetails.transportTab.companies')
                        })
                }, render: ({ field }) => (_jsx(ControlledHCheckboxAutocomplete, { control: control, ...field, bg: 'grey8', name: 'companies', options: companyOptions, label: t('events.eventDetails.transportTab.companies'), error: errors.companies?.message, getOptionKey: (option) => String(option.value), showSelectAll: true, checkboxMode: true, maxTags: 2, groupBy: (option) => option.regionLabel || '', renderGroup: (params) => {
                        const regionLabel = params.group;
                        const regionCompanyIds = regionGroups[regionLabel] || [];
                        const currentCompanies = field.value || [];
                        const currentCompanyIds = currentCompanies.map((c) => String(typeof c === 'object' && c.value ? c.value : c));
                        const allSelected = regionCompanyIds.length > 0 &&
                            regionCompanyIds.every((id) => currentCompanyIds.includes(id));
                        const someSelected = !allSelected && regionCompanyIds.some((id) => currentCompanyIds.includes(id));
                        const isUngrouped = !regionLabel;
                        return (_jsxs("li", { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', sx: { pr: 1 }, onClick: (e) => e.stopPropagation(), children: [_jsx(Typography, { variant: 'subtitle2', sx: { pl: 2, pt: 1, pb: 0.5, color: 'text.secondary' }, children: regionLabel }), !isUngrouped && (_jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, onClick: (e) => {
                                                e.stopPropagation();
                                                handleCompanyRegionToggle(regionLabel, field);
                                            } }))] }), _jsx("ul", { style: { padding: 0, margin: 0 }, children: params.children })] }, params.key));
                    } })) }), VDKEventsForEventDetails?.type !== EventTypeEnum.Family && (_jsx(SingleSelect, { bg: 'grey8', control: control, name: 'supplier', label: t('events.eventDetails.transportTab.supplier'), options: supplierOptions, error: errors.supplier?.message, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.supplier')
                    })
                } })), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'isAccommodationAvailable', label: t('events.eventDetails.transportTab.accommodationAvailable'), showSelectedIcon: true, disableClearable: true, options: [
                    { label: t('events.toggleOptions.yes') || 'Yes', value: true },
                    { label: t('events.toggleOptions.no') || 'No', value: false }
                ], error: errors.isAccommodationAvailable?.message, rules: {
                    validate: (value) => (value === true || value === false) ||
                        t('validations.required', {
                            field: t('events.eventDetails.transportTab.accommodationAvailable')
                        })
                } }), _jsx(Controller, { name: 'timeOfPickup', control: control, rules: {
                    validate: (value) => {
                        if (VDKEventsForEventDetails?.type === EventTypeEnum.Family && !value) {
                            return t('validations.required', {
                                field: t('events.eventDetails.transportTab.timeOfPickup')
                            });
                        }
                        return true;
                    }
                }, render: ({ field, fieldState }) => (_jsxs(Box, { children: [_jsx(HTimePicker, { value: field.value, handleSelectedTime: (time) => field.onChange(time), label: t('events.eventDetails.transportTab.timeOfPickup'), labelTooltipText: t('events.eventDetails.transportTab.timeOfPickup24h'), bg: 'grey8' }), fieldState.error && _jsx(FormValidationMessage, { error: fieldState.error.message || '' })] })) })] }));
    const dialogActions = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', onClick: handleCancel, size: 'large', fullWidth: true, disabled: isSubmitting, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', onClick: handleSubmit(onSubmit), size: 'large', fullWidth: true, disabled: isSubmitting || isPlaceDetailsLoading, children: isEditMode ? t('actions.saveChanges') : t('actions.createNow') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: handleCancel, title: t('events.eventDetails.transportTab.newPickupLocation'), anchor: 'bottom', showBottomBar: true, dialogActions: dialogActions, children: _jsx(Box, { sx: { p: 2 }, children: formContent }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleCancel, titleWithIcon: isEditMode ? t('events.eventDetails.transportTab.editPickupLocation') : t('events.eventDetails.transportTab.newPickupLocation'), titleIcon: '/assets/images/location-pin.png', maxWidth: 'sm', backgroundVariant: 'white', closeIcon: true, actions: dialogActions, children: _jsx(Box, { p: 1.5, sx: {
                maxHeight: '65vh',
                overflowY: 'auto'
            }, children: formContent }) }));
};
export default NewPickupLocationDialog;
